import { gql } from '@apollo/client'

// GraphQL contract for the golfer side of Virtual Coaching. The backend for
// these operations (pga-backend #12084) is merged to pga-backend main; confirm
// the prod deploy before enabling the flag. See
// .agents/skills/coach-virtual-coaching/SKILL.md ("Golfer side") for the
// assumptions and which ones are guesses.
//
// The fragment name differs from the coach side's `VirtualCoachingRequestFields`
// because the two select different fields, and Apollo warns about duplicate
// fragment names.

export const STUDENT_VIRTUAL_COACHING_REQUEST_FIELDS = gql`
  fragment StudentVirtualCoachingRequestFields on VirtualCoachingRequest {
    id
    orderNumber
    status
    dueAt
    videoUrls
    videoThumbnailUrls
    golfLevel
    struggle
    goal
    lessonType {
      id
      title
      priceInCents
      virtualCoaching {
        turnaroundHours
      }
      coach {
        name
        externalId
        coachProfile {
          slug
          title
          profilePhoto
        }
      }
    }
    response {
      videoUrl
      onformUrl
      thumbnailUrl
      notes
      sharedAt
    }
  }
`

export const VIRTUAL_COACHING_OFFERING = gql`
  query VirtualCoachingOffering($lessonTypeId: ID!) {
    virtualCoachingOffering(lessonTypeId: $lessonTypeId) {
      id
      title
      description
      priceInCents
      offeringKind
      virtualCoaching {
        turnaroundHours
        expiresAt
      }
      coach {
        name
        externalId
        coachProfile {
          slug
          title
          profilePhoto
        }
      }
    }
  }
`

// Same shape as `preparePurchaseLessonPack` (see
// pages/schedule/BuyLessonPacksLayout/query.js).
export const PREPARE_PURCHASE_VIRTUAL_COACHING = gql`
  mutation PreparePurchaseVirtualCoaching($lessonTypeId: ID!) {
    preparePurchaseVirtualCoaching(lessonTypeId: $lessonTypeId) {
      success
      message
      code
      paymentIntent {
        clientSecret
      }
    }
  }
`

export const MY_VIRTUAL_COACHING_REQUESTS = gql`
  query MyVirtualCoachingRequests {
    myVirtualCoachingRequests {
      ...StudentVirtualCoachingRequestFields
    }
  }
  ${STUDENT_VIRTUAL_COACHING_REQUEST_FIELDS}
`

// `transaction { id }` is selected here only: the detail page is the one reader
// (it links the order number to the transaction page). Keeping it out of the
// shared fragment means a schema without it cannot break the list, the nav
// query, the confirmation lookup, or the upload mutation.
export const MY_VIRTUAL_COACHING_REQUEST = gql`
  query MyVirtualCoachingRequest($id: ID!) {
    myVirtualCoachingRequest(id: $id) {
      ...StudentVirtualCoachingRequestFields
      transaction {
        id
      }
    }
  }
  ${STUDENT_VIRTUAL_COACHING_REQUEST_FIELDS}
`

// A separate document from the shared `ProductByPaymentIntentId` query in
// pages/product-purchase-confirmation/query.js: spreading
// `... on VirtualCoachingRequest` there would make that query invalid (and
// break every other purchase confirmation) until the backend adds the type to
// the `productByPaymentIntentId` union.
export const VIRTUAL_COACHING_PRODUCT_BY_PAYMENT_INTENT_ID = gql`
  query VirtualCoachingProductByPaymentIntentId($paymentIntentId: String!) {
    productByPaymentIntentId(paymentIntentId: $paymentIntentId) {
      ... on VirtualCoachingRequest {
        ...StudentVirtualCoachingRequestFields
      }
    }
  }
  ${STUDENT_VIRTUAL_COACHING_REQUEST_FIELDS}
`

export const SUBMIT_VIRTUAL_COACHING_VIDEOS = gql`
  mutation SubmitVirtualCoachingVideos(
    $requestId: ID!
    $videoUrls: [String!]!
    $videoThumbnailUrls: [String!]
    $golfLevel: String!
    $struggle: String!
    $goal: String!
  ) {
    submitVirtualCoachingVideos(
      requestId: $requestId
      videoUrls: $videoUrls
      videoThumbnailUrls: $videoThumbnailUrls
      golfLevel: $golfLevel
      struggle: $struggle
      goal: $goal
    ) {
      ...StudentVirtualCoachingRequestFields
    }
  }
  ${STUDENT_VIRTUAL_COACHING_REQUEST_FIELDS}
`
